import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import React, { Fragment, useEffect, useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import { VscLoading } from 'react-icons/vsc';
import { toast } from 'react-toastify';
import { ToolTip } from '../../../../_elements/ToolTip';
import { fetchWrapper, roundDown, timeConvert, getBadgeClass, DateTimeSorter } from '../../../../_helpers';
import { ActionStyles } from '../../reusableStyles';
import { BsShieldFillX } from 'react-icons/bs';
import { FaUndoAlt, FaWpforms } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useLocation } from 'react-router-dom';
import CompleteDraftVisitModal from './CompleteDraftVisitModal';
import {
    VisitChangeLabelAction,
    VisitRedirectToChannel,
    VisitStatus,
    VisitView,
} from './components/VisitRowActionButtons';
import { changeVisitLabel } from '@/_redux/actions/ActionEVV';

const PillBadge = styled.span`
    padding: 0.5rem 1rem;
    background: #50aeb0;
    border-radius: 100px;
    margin-left: 1rem;
    color: white;
    cursor: default;
    &.blue {
        background: #f5f5f5;
        color: #111;
    }
`;

export const ActionTD = styled.td`
    display: flex;
    justify-content: end;
    gap: 0;
    min-width: 80px;
`;

function VisitRow({
    visit,
    handleShow,
    visitVerifyMode,
    setVerifyVisitsId,
    labels,
    team,
    provider,
    teamDetails,
    changeVisitLabel,
}) {
    const isPayroll = !!useLocation().pathname.match(/payroll/);
    const [billable, setBillable] = useState(visit?.extraInfo?.BillVisit ?? false);
    const [visitData, setVisitData] = useState(visit);
    const [isFormsLoading, setIsFormsLoading] = useState(false);
    const handleUpdateVisitRow = (updatedVisit) => {
        setVisitData((prev) => ({
            ...prev,
            ...updatedVisit,
            extraInfo: { ...prev.extraInfo, ...updatedVisit.extraInfo },
        }));
    };

    const handleShowForms = async () => {
        if (visitData.forms && visitData.forms.length > 0 && typeof visitData.forms[0] === 'object') {
            handleShow({ ...visitData, handleUpdateVisitRow }, 'forms');
            return;
        }

        try {
            setIsFormsLoading(true);
            const response = await fetchWrapper.get(
                `userFormData?visit=${visitData.id || visitData._id}&limit=30`
            );
            const preparedFormsData = (response?.results || []).map(item => {
                const formData = item?.formData || {};
                return {
                    ...item,
                    formData: formData?.data || [],
                    name: formData?.name || ''
                }
            }).sort(DateTimeSorter)
            const updatedVisit = { ...visitData, forms: preparedFormsData };
            setVisitData(updatedVisit);
            handleShow({ ...updatedVisit, handleUpdateVisitRow }, 'forms');
        } catch (err) {
            console.error(err);
            toast.error('Failed to fetch forms');
        } finally {
            setIsFormsLoading(false);
        }
    };


    useEffect(() => {
        let distance = (visit?.extraInfo?.mileageData?.distance || 0) / 1609.34;
        const reimbursementAmount =
            ((visit?.extraInfo?.mileageData?.distance || 0) *
                (provider?.payroll?.metadata?.mileageReimbursementRate || 0)) /
            1609.34;
        setVisitData({
            ...visitData,
            label: visit?.extraInfo?.label,
            status: visit?.status || 'complete',
            distance,
            reimbursementAmount,
        });
    }, [visit]);

    const [isLabelChanging, setIsLabelChanging] = useState(false);
    const handleBillableSuccess = (billStatus) => {
        setBillable(JSON.parse(billStatus));
    };

    const handleLabelChange = (newLabel) => {
        const modifyVisit = async () => {
            try {
                setIsLabelChanging(true);
                const response = await changeVisitLabel({
                    visitId: visitData.id || visitData._id,
                    label: newLabel,
                    team,
                });
                setVisitData((prev) => ({
                    ...prev,
                    label: response?.label || newLabel,
                    status: response?.status || prev?.status,
                }));
            } catch (err) {
                console.log(err);
                toast.error(err?.message || 'Something went wrong');
            } finally {
                setIsLabelChanging(false);
            }
        };
        modifyVisit();
    };

    const handleCompleteVisit = () => {
        const modifyVisit = async () => {
            try {
                setIsLabelChanging(true);
                const body = {
                    visit: visitData.id || visitData._id,
                };
                const response = await fetchWrapper.patch(`evv/visit/complete?${team ? `&team=${team}` : ''}`, body);
                setVisitData((prev) => ({ ...prev, ...response }));
                toast.success('Visit completed successfully');
            } catch (err) {
                console.log(err);
                toast.error(err.message || 'Something went wrong');
            } finally {
                setIsLabelChanging(false);
            }
        };
        const message =
            visitData?.status === 'draft'
                ? 'Completing a visit will automatically send your information to external EVV systems like Sandata and Therap.'
                : 'Reverting payment status will not update already created checks with this visit.';
        Swal.fire({
            title: 'Are you sure?',
            html: `<div style="font-size:16px;color:brown;text-align:justify;width:100%;">${message}</div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#50aeb0',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                modifyVisit();
            }
        });
    };

    return (
        <tr
            className={` ${visitData?.label ? visitData?.label : ''}`}
            css={css`
                background: ${isPayroll ? '' : labels[visitData?.label]};
            `}
        >
            {visitVerifyMode && (
                <td>
                    {visitData.verified ? (
                        <Button variant="danger" onClick={() => alert('Work on progress')}>
                            <BsShieldFillX fontSize={20} />
                        </Button>
                    ) : (
                        <Form.Check onChange={() => setVerifyVisitsId(visitData.id)} />
                    )}
                </td>
            )}
            <td className={`${visitData.client ? '' : 'text-danger'}`}>
                {visitData.client?._id ? (
                    <div>
                        <div>{visitData.client?.firstName + ` ` + visitData.client?.lastName}</div>
                        {visitData.client.archieved && <Badge variant="danger">Archived</Badge>}
                    </div>
                ) : (
                    'Deleted Client'
                )}
            </td>
            <td>
                {visitData.provider?._id ? (
                    <div>
                        <div>{visitData.provider.agencyName}</div>
                        <div>{visitData.provider.archieved && <Badge variant="danger">Archived</Badge>}</div>
                    </div>
                ) : (
                    <span className="text-danger">Agency deleted</span>
                )}
            </td>
            <td>
                {visitData.employee?._id ? (
                    <ToolTip text={visitData.employee?.firstName + ` ` + visitData.employee?.lastName} side="top">
                        <div>{visitData.employee?.user?.name}</div>

                        {visitData.employee.archieved && <Badge variant="danger">Archived</Badge>}
                    </ToolTip>
                ) : (
                    <span className="text-danger">Employee deleted</span>
                )}
            </td>
            <td>{dayjs(visitData.ScheduleStartTime).format('MM-DD-YYYY, h:mm A')}</td>
            <td>{dayjs(visitData.ScheduleEndTime).format('MM-DD-YYYY, h:mm A')}</td>

            {isPayroll ? (
                <Fragment>
                    <td>{visitData?.distance?.toFixed(2)}</td>
                    <td>${visitData?.reimbursementAmount?.toFixed(2)}</td>
                    <td>
                        <span
                            className={`badge badge-pill ${getBadgeClass(visitData?.extraInfo?.reimbursementStatus || 'draft')}`}
                        >
                            {visitData?.extraInfo?.reimbursementStatus || 'draft'}
                        </span>
                    </td>
                </Fragment>
            ) : (
                <Fragment>
                    <td>
                        {timeConvert(dayjs(visitData.ScheduleEndTime).diff(dayjs(visitData.ScheduleStartTime), 's'))}
                        {!isPayroll && (
                            <Fragment>
                                {visitData.isManual && <PillBadge>Manual</PillBadge>}
                                {visitData.extraInfo?.backupForm && <PillBadge>BackupForm</PillBadge>}
                                {visitData?.extraInfo?.offlineMode && <PillBadge>Offline</PillBadge>}
                                {visitData?.extraInfo?.bulkPush && <PillBadge>Bulkpush</PillBadge>}
                            </Fragment>
                        )}
                    </td>
                    <td>
                        {roundDown(
                            dayjs(visitData.ScheduleEndTime).diff(dayjs(visitData.ScheduleStartTime), 'm'),
                            visit?.extraInfo?.minutesPerUnit
                        )}
                    </td>
                    <td>
                        <input
                            css={css`
                                width: 20px;
                                height: 20px;
                            `}
                            type="checkbox"
                            checked={billable}
                            onChange={(e) => {
                                handleShow({ ...visit, NewBillVisit: !billable, handleBillableSuccess }, 'donotbill');
                            }}
                            disabled={!(visit?.extraInfo?.sendTo === 'sandata') || !visit?.extraInfo?.SequenceID}
                        />
                    </td>
                    <td>
                        <div
                            className="d-flex align-items-center"
                            css={css`
                                gap: 6px;
                            `}
                        >
                            <VisitStatus visitData={visitData} isLabelChanging={isLabelChanging} />
                            {['payment_pending', 'draft'].includes(visitData?.status) && (
                                <ToolTip
                                    text="Complete Visit"
                                    side="right"
                                    cssStyles={`top:-70% !important; min-width: 126px!important;`}
                                >
                                    <ActionStyles>
                                        {isLabelChanging ? (
                                            <span className="icon edit d-flex justify-content-center align-items-center ">
                                                <VscLoading className=" spin" />
                                            </span>
                                        ) : visitData?.status === 'draft' ? (
                                            <Fragment>
                                                <CompleteDraftVisitModal
                                                    visitData={visitData}
                                                    handleUpdateVisitRow={handleUpdateVisitRow}
                                                    team={team}
                                                />
                                                {/* <HiCheck className="icon" onClick={handleCompleteVisit} /> */}
                                            </Fragment>
                                        ) : (
                                            <FaUndoAlt className="icon" onClick={handleCompleteVisit} />
                                        )}
                                    </ActionStyles>
                                </ToolTip>
                            )}
                        </div>
                    </td>
                </Fragment>
            )}

            <ActionTD className="action">
                {!isPayroll && <VisitView visit={visit} visitData={visitData} handleShow={handleShow} />}
                {visit?.forms?.length ? (
                    <ToolTip
                        text="Form Submissions"
                        side="left"
                        cssStyles={`top:-70% !important; min-width: 126px !important; left: -15px !important;`}
                    >
                        <ActionStyles className="view-icon">
                            {isFormsLoading ? (
                                <div className='icon'>
                                    <VscLoading className="spin mb-2" />
                                </div>
                            ) : (
                                <FaWpforms className="icon" onClick={handleShowForms} />
                            )}
                        </ActionStyles>
                    </ToolTip>
                ) : null}
                {isPayroll ? null : (
                    <Fragment>
                        <VisitChangeLabelAction
                            isLabelChanging={isLabelChanging}
                            onChangeLabel={handleLabelChange}
                            labels={labels}
                            teamDetails={teamDetails}
                            teamId={team}
                        />
                        <VisitRedirectToChannel visit={visitData} />
                    </Fragment>
                )}
            </ActionTD>
        </tr>
    );
}

const mapStateToProps = () => ({});

export default connect(mapStateToProps, { changeVisitLabel })(VisitRow);
